import { useQuery } from '@tanstack/react-query';
import { useRica } from '../../lib/api/hooks-rica.js';
import { api } from '../../lib/api/client.js';
import { useCdrStats, useExtensions } from '../../lib/api/hooks.js';
import { useTrunkStatus } from '../../lib/api/hooks-products.js';
import { useRingGroups, useQueues, useIvrs, useNumbers } from '../../lib/api/hooks-tms.js';
import { SpendInsights } from './SpendInsights.js';
import { Stat, StatGroup, ErrorBanner, CardHead, SectionHead } from '../../components/ui/Primitives.js';
import { Icon, type IconName } from '../../components/ui/Icon.js';
import { VolumeChart, type SeriesPoint } from '../../components/charts/Charts.js';
import { LiveAccountProvider, useLiveState } from '../live/useLiveState.js';
import { presenceOf } from '../extensions/ExtensionPresence.js';
import { formatMoney, formatDuration, formatNumber } from '../../lib/format.js';
import { useSession } from '../../lib/auth/session.js';
import type { Tenant } from '@arcnet/contracts';
import { FraudHoldBanner } from '../fraud/FraudHoldBanner.js';
import { SpendBanner } from '../billing/SpendLimits.js';


/**
 * The account's landing page.
 *
 * Answers the questions an operator or a customer opens the portal with -- "is anything wrong",
 * "is it working right now", "is it busy and what is it costing" -- and then offers a way into the
 * settings that matter, so the dashboard is a starting point rather than a dead end. Rebuilt from
 * the UX review mockup (21 October 2026): one Needs attention card, a Right now row, no decorative
 * colour.
 */
export function AccountDashboard(props: {
  tenantId: string;
  tenant: Tenant | null;
  onNavigate: (section: string) => void;
}) {
  // The live stream is about this account, whoever is looking (staff inside its console too).
  return <LiveAccountProvider value={props.tenantId}><Dashboard {...props} /></LiveAccountProvider>;
}

/** What is live this minute: calls, callers waiting, and queues with nobody free to answer. */
function useRightNow() {
  const s = useLiveState(['calls', 'queues']);
  return {
    streamed: s.streamed,
    onCall: s.extensions.filter((e) => e.state === 'on_call').length,
    ringing: s.extensions.filter((e) => e.state === 'ringing').length,
    waiting: s.queues.reduce((n, q) => n + q.waiting, 0),
    queues: s.queues.filter((q) => q.waiting > 0).length,
    unstaffed: s.queues.filter((q) => q.waiting > 0 && q.agents_available === 0),
  };
}

function Dashboard({ tenantId, tenant, onNavigate }: {
  tenantId: string;
  tenant: Tenant | null;
  onNavigate: (section: string) => void;
}) {
  const live = useRightNow();
  // A tile only for a section the role can open (config-nav.ts; permission sweep, 19 September 2026).
  const can = useSession((s) => s.can);
  // Fetches too: a figure the role cannot read is not asked for.
  const mine = (perm: string): string => (can(perm) ? tenantId : '');
  const stats = useCdrStats(mine('cdr:read') || null);
  // Only staff link trunks; a customer is sent to see the status instead.
  const canLink = useSession((s) => s.can)('trunk:write');
  const series = useQuery({
    queryKey: ['cdr-series', tenantId],
    queryFn: () => api.get<{ days: number; data: SeriesPoint[] }>(
      `/v1/tenants/${tenantId}/cdrs/series?days=30`),
    enabled: !!tenantId && can('cdr:read'),
  });

  const extensions = useExtensions(mine('extension:read') || null, { limit: 200 });
  /**
   * The customer-safe trunk view. This used the staff endpoint, which refuses a
   * customer -- so on a customer's own dashboard the trunk tile said
   * "Checking…" for ever and the no-carrier warning could never appear.
   */
  const trunks = useTrunkStatus(mine('connectivity:read') || null);
  const groups = useRingGroups(mine('extension:read'));
  const queues = useQueues(mine('queue:read'));
  const ivrs = useIvrs(mine('flow:read'));
  const numbers = useNumbers(mine('number:read'));

  const points = series.data?.data ?? [];
  const currency = stats.data?.currency ?? tenant?.currency ?? 'ZAR';
  const busiest = points.reduce<SeriesPoint | null>(
    (best, p) => (!best || p.total > best.total ? p : best), null);

  /**
   * Warnings wait for an ANSWER, not for the absence of one.
   *
   * These read `trunks.data?...?? []`, so while the request was in flight the
   * list was empty and "No outbound carrier — external calls will fail" was on
   * screen. It then vanished when the data arrived and showed a trunk was
   * linked all along. It looked like a banner that disappeared; it was a false
   * alarm that corrected itself.
   *
   * Keeping it until dismissed, which is the obvious fix, would have made the
   * false alarm permanent instead. The banner has to know the difference
   * between "no trunks" and "not asked yet".
   */
  const trunksKnown = trunks.isSuccess;
  const numbersKnown = numbers.isSuccess;
  const outbound = trunks.data?.data.filter((t) => t.direction !== 'inbound' && t.is_enabled) ?? [];
  const unrouted = (numbers.data?.data ?? []).filter((n) => !n.route);
  // As the switch measured them, seconds ago: a carrier that stopped answering,
  // or a registration that is failing, matters more than any number below.
  const inUse = (trunks.data?.data ?? []).filter((t) => t.is_enabled && t.trunk_status === 'active');
  const down = inUse.filter((t) => t.health === 'down');
  const unregistered = inUse.filter((t) => t.registration === 'failing');
  const trunkHint = !trunksKnown ? 'Checking…'
    : outbound.length === 0 ? 'No outbound carrier'
    : down.length > 0 ? `${down[0]!.carrier_name} not answering`
    : unregistered.length > 0 ? `Not registered with ${unregistered[0]!.carrier_name}`
    : inUse.length > 0 && inUse.every((t) => t.health === 'up')
      ? `${inUse.map((t) => t.carrier_name).join(', ')} · up${inUse.some((t) => t.registration === 'registered') ? ', registered' : ''}`
    : outbound.length === 1 ? 'Single carrier — no failover' : `${outbound.length} outbound`;

  /** Phones down right now: seats that were registered and are not (never-set-up seats are not a fault). */
  const seats = extensions.data?.data ?? [];
  const offline = seats.filter((e) => presenceOf(e).state === 'offline');
  const neverSeen = seats.filter((e) => presenceOf(e).state === 'never');
  const fmtDay = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
  const named = (xs: Array<{ name: string }> | undefined, none: string) => {
    if (!xs || xs.length === 0) return none;
    const shown = xs.slice(0, 2).map((x) => x.name).join(', ');
    return xs.length > 2 ? `${shown} +${xs.length - 2}` : shown;
  };

  /**
   * Everything wrong right now, in one card, one row and one action each (UX review mockup,
   * 21 October 2026). It was three banners built by hand, and missed a queue with callers and nobody
   * to answer, and phones that dropped off. Absent when nothing is wrong: banners only for problems.
   */
  /** RICA verification not approved (0170): what it pauses, and where to finish it. */
  const rica = useRica(tenantId, can('rica:read'));
  const ricaOpen = rica.data && rica.data.status !== 'approved' ? rica.data.status : null;
  const problems: Array<{ key: string; tone: 'danger' | 'warn'; title: string; detail: string; action: string; go: string }> = [
    ...(ricaOpen ? [{
      key: 'rica', tone: ricaOpen === 'rejected' ? 'danger' as const : 'warn' as const,
      title: ricaOpen === 'submitted' ? 'RICA verification is with the reviewer' : ricaOpen === 'rejected' ? 'RICA documents were sent back' : 'RICA verification is needed',
      detail: 'New numbers, airtime and calls to outside numbers are paused until it is approved. Internal and emergency calls work.',
      action: ricaOpen === 'submitted' ? 'View' : 'Upload documents', go: 'rica',
    }] : []),
    ...(trunksKnown && outbound.length === 0 ? [{
      key: 'carrier', tone: 'danger' as const, title: 'No outbound carrier',
      detail: `External calls will fail${canLink ? '' : ' until your provider connects a trunk'}.`,
      action: canLink ? 'Link a trunk' : 'Trunks', go: 'trunks',
    }] : []),
    ...(trunksKnown && (down.length > 0 || unregistered.length > 0) ? [{
      key: 'down', tone: 'danger' as const,
      title: down.length > 0 ? `${down.map((t) => t.carrier_name).join(', ')} is not answering`
        : `Not registered with ${unregistered.map((t) => t.carrier_name).join(', ')}`,
      detail: down.length > 0 ? 'Calls over this carrier will fail until it answers again.'
        : 'Incoming calls from this carrier cannot reach you until the registration succeeds.',
      action: 'Trunk status', go: 'trunks',
    }] : []),
    ...(numbersKnown && unrouted.length > 0 ? [{
      key: 'unrouted', tone: 'warn' as const,
      title: `${unrouted.length} number${unrouted.length === 1 ? '' : 's'} with no route`,
      detail: 'Callers reach nothing.', action: 'Set routes', go: 'numbers',
    }] : []),
    ...(live.unstaffed.length > 0 ? [{
      key: 'queue', tone: 'danger' as const,
      title: live.unstaffed.length === 1 ? `${live.unstaffed[0]!.name} has callers waiting and no agent ready`
        : `${live.unstaffed.length} queues have callers waiting and no agent ready`,
      detail: 'Those callers wait until an agent is free or the queue times out.', action: 'Live queues', go: 'live-queues',
    }] : []),
    ...(offline.length > 0 ? [{
      key: 'offline', tone: 'warn' as const,
      title: `${offline.length} phone${offline.length === 1 ? '' : 's'} offline`,
      detail: offline.length <= 3 ? offline.map((e) => `${e.number} ${e.display_name}`).join(', ') : 'They were registered and have stopped.',
      action: 'Extensions', go: 'extensions',
    }] : []),
  ];

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Overview</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Right now, and the last 30 days · times in {tenant?.timezone ?? 'Africa/Johannesburg'}
          </p>
        </div>
      </div>

      <FraudHoldBanner tenantId={tenantId} />
      <SpendBanner tenantId={tenantId} />
      {stats.error && <ErrorBanner error={stats.error} />}

      {/* Problems before the numbers: healthy totals on an account that cannot make an outside
          call are actively misleading. */}
      {problems.length > 0 && (
        <div className="arc-card arc-attention" style={{ marginBottom: 14 }} role="alert">
          <CardHead title="Needs attention" />
          <ul className="arc-attention-list">
            {problems.map((p) => (
              <li key={p.key} data-tone={p.tone}>
                <Icon name="warning" size={14} />
                <div className="arc-stack" style={{ minWidth: 0, flex: 1 }}>
                  <strong>{p.title}</strong>
                  <span className="arc-subtle" style={{ fontSize: 12.5 }}>{p.detail}</span>
                </div>
                <button type="button" className="arc-btn arc-btn-sm" onClick={() => onNavigate(p.go)}>{p.action}</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* What is happening this minute, from the same live stream as Real-time. */}
      <SectionHead level="section" title="Right now" />
      <div style={{ marginBottom: 18 }}>
        <StatGroup>
          <Stat bare label="On a call" value={live.streamed ? formatNumber(live.onCall) : '—'}
            sub={live.streamed && live.ringing > 0 ? `${live.ringing} ringing` : undefined} />
          <Stat bare label="Callers waiting" value={live.streamed ? formatNumber(live.waiting) : '—'}
            tone={live.unstaffed.length > 0 ? 'danger' : undefined}
            sub={live.streamed && live.queues > 0 ? `in ${live.queues} queue${live.queues === 1 ? '' : 's'}` : undefined} />
          <Stat bare label="Phones online"
            value={extensions.data ? `${seats.length - offline.length - neverSeen.length} of ${seats.length}` : '—'}
            tone={offline.length > 0 ? 'warn' : undefined}
            sub={offline.length > 0 ? `${offline.length} offline` : neverSeen.length > 0 ? `${neverSeen.length} never set up` : undefined} />
          <Stat bare label="Carriers" value={!trunksKnown ? '—' : outbound.length === 0 ? 'none' : down.length + unregistered.length > 0 ? 'fault' : 'up'}
            tone={trunksKnown && (outbound.length === 0 || down.length + unregistered.length > 0) ? 'danger' : undefined}
            sub={trunksKnown ? trunkHint : undefined} />
        </StatGroup>
      </div>

      <SectionHead level="section" title="Last 30 days" />
      <div style={{ marginBottom: 14 }}>
        <StatGroup>
          {/*
            An em-dash while loading, never a zero.

            `?? 0` renders a confident "0 calls", "0:00 talk time" and
            "0 extensions in use" for the second or two before the answers
            arrive -- and "no extensions on this account" is a statement an
            operator might act on. Spend was already honest about it, because
            formatMoney(null) returns a dash; the other three were not, so a
            page caught mid-load looked like a dead account.

            No coloured icons (UX review, 21 October 2026): colour on a figure means its state.
          */}
          <Stat bare label="Calls" value={stats.data ? formatNumber(stats.data.total_calls) : '—'}
            sub={stats.data ? `${formatNumber(stats.data.inbound)} in · ${formatNumber(stats.data.outbound)} out${busiest ? ` · busiest ${fmtDay(busiest.day)}` : ''}` : undefined} />
          <Stat bare label="Answered" value={stats.data ? `${Math.round(stats.data.answer_rate_pct)}%` : '—'}
            sub={stats.data ? `${formatNumber(stats.data.answered_calls)} of all calls` : undefined} />
          <Stat bare label="Talk time"
            value={stats.data ? formatDuration(stats.data.total_duration_sec) : '—'}
            sub={stats.data ? `avg ${formatDuration(stats.data.avg_duration_sec)}` : undefined} />
          <Stat bare label="Spend" value={formatMoney(stats.data?.spend ?? null, currency)}
            sub="outbound usage, excl. VAT" />
          <Stat bare label="Extensions"
            value={extensions.data
              ? `${seats.length}${tenant?.max_extensions ? ` of ${tenant.max_extensions}` : ''}`
              : '—'}
            sub={extensions.data ? (neverSeen.length > 0 ? `${neverSeen.length} never set up` : 'all set up') : undefined} />
        </StatGroup>
      </div>

      <div className="arc-card" style={{ marginBottom: 14 }}>
        <CardHead title="Calls per day" sub="Inbound and outbound, last 30 days" />
        <div className="arc-card-body">
          {series.error ? <ErrorBanner error={series.error} />
            : series.isLoading
              ? <div className="arc-skeleton" style={{ height: 150 }} />
              : <VolumeChart data={points} />}
        </div>
      </div>

      {/* This answers "where did the money go", the question that follows "how much did we spend". */}
      <SectionHead level="section" title="Where the spend went" />
      <div style={{ marginBottom: 18 }}>
        <SpendInsights tenantId={tenantId} />
      </div>

      <SectionHead level="section" title="Configuration" />
      {/* Each tile says something true about this account, not what the section is for. */}
      <div className="arc-kind-grid">
        {can('extension:read') && <Tile icon="extension" label="Extensions" count={extensions.data?.data.length}
          hint={!extensions.data ? 'Checking…' : offline.length > 0 ? `${offline.length} offline` : `${seats.length - neverSeen.length} with a phone`}
          warn={offline.length > 0} onClick={() => onNavigate('extensions')} />}
        {can('number:read') && <Tile icon="hash" label="Numbers" count={numbers.data?.data.length}
          hint={unrouted.length ? `${unrouted.length} without a route` : 'All routed'}
          warn={unrouted.length > 0} onClick={() => onNavigate('numbers')} />}
        {can('extension:read') && <Tile icon="users" label="Hunt lists" count={groups.data?.data.length}
          hint={named(groups.data?.data, 'None yet')} onClick={() => onNavigate('ring-groups')} />}
        {can('queue:read') && <Tile icon="queue" label="Queues" count={queues.data?.data.length}
          hint={named(queues.data?.data, 'None yet')} onClick={() => onNavigate('queues')} />}
        {can('flow:read') && <Tile icon="ivr" label="IVR menus" count={ivrs.data?.data.length}
          hint={named(ivrs.data?.data, 'None yet')} onClick={() => onNavigate('ivrs')} />}
        {can('connectivity:read') && <Tile icon="carrier" label="Trunks" count={trunks.data?.data.length}
          hint={trunkHint}
          warn={trunksKnown && (outbound.length === 0 || down.length > 0 || unregistered.length > 0)}
          onClick={() => onNavigate('trunks')} />}
        {can('cdr:read') && <Tile icon="phone" label="Call history"
          hint={stats.data ? `${formatNumber(stats.data.total_calls)} calls in 30 days` : 'Every call, with quality'}
          onClick={() => onNavigate('calls')} />}
        {can('billing:read') && <Tile icon="billing" label="Billing" hint="Balance & payments"
          onClick={() => onNavigate('billing')} />}
      </div>
    </>
  );
}

function Tile({ icon, label, hint, count, warn, onClick }: {
  icon: IconName; label: string; hint: string;
  count?: number | undefined; warn?: boolean; onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className="arc-kind">
      <span className="arc-kind-icon"><Icon name={icon} size={17} /></span>
      <span className="arc-kind-body">
        <span className="arc-kind-label">{label}</span>
        <span className={`arc-kind-hint${warn ? ' arc-error' : ''}`} title={hint}>
          {warn && <Icon name="warning" size={10} style={{ display: 'inline', marginRight: 3 }} />}
          {hint}
        </span>
      </span>
      {count !== undefined && <span className="arc-kind-count">{count}</span>}
    </button>
  );
}
